import * as React from 'react';
import { Button } from '@/components/ui/button';
import { PenIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { InterviewData } from '@/lib/interviewTypes';
import { useUpdateInterviewMutation, useCancelInterviewMutation } from '@/store/instructor/interview';
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogTrigger,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogAction,
    AlertDialogCancel,
} from '@/components/ui/alert-dialog'; // ← Your custom one!

interface InstructorActionsProps {
    interview: InterviewData;
    token?: string | null;
}

const InterviewCardInstructorActions: React.FC<InstructorActionsProps> = ({
    interview,
    token,
}) => {
    const initialScheduledFor = React.useMemo(() => {
        try {
            return format(new Date(interview.scheduledDate), "yyyy-MM-dd'T'HH:mm");
        } catch {
            return '';
        }
    }, [interview.scheduledDate]);

    const [scheduledFor, setScheduledFor] = React.useState<string>(initialScheduledFor);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);

    const [updateInterview, { isLoading: isUpdating }] = useUpdateInterviewMutation();
    const [cancelInterview, { isLoading: isCancelling }] = useCancelInterviewMutation();

    const handleUpdate = async () => {
        if (!token) {
            toast.error('You must be logged in');
            return;
        }
        if (!scheduledFor) {
            toast.error('Choose a new date and time');
            return;
        }

        try {
            const newDate = new Date(scheduledFor).toISOString();
            await updateInterview({
                id: interview._id,
                Token: token,
                formData: { scheduledDate: newDate },
            }).unwrap();
            toast.success('Interview updated');
            setIsDialogOpen(false);
        } catch (error: any) {
            console.error('Update interview error', error);
            toast.error(error?.data?.message || error?.message || 'Failed to update interview');
        }
    };

    const handleCancel = async () => {
        if (!token) {
            toast.error('You must be logged in');
            return;
        }

        try {
            await cancelInterview({ id: interview._id, Token: token }).unwrap();
            toast.success('Interview cancelled');
        } catch (error: any) {
            console.error('Cancel interview error', error);
            toast.error(error?.data?.message || error?.message || 'Failed to cancel interview');
        }
    };

    const handleDialogOpenChange = (open: boolean) => {
        setIsDialogOpen(open);
        if (!open) {
            setScheduledFor(initialScheduledFor);
        }
    };

    return (
        <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-2">
                <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
                    <DialogTrigger asChild>
                        <Button
                            size="sm"
                            className="flex items-center gap-2 rounded-md bg-emerald-500 text-white shadow-sm transition-colors hover:bg-emerald-600 focus-visible:ring-emerald-500 cursor-pointer dark:bg-emerald-500 dark:hover:bg-emerald-600 dark:text-gray-100"
                        >
                            <PenIcon className="w-4 h-4" />
                            <span>Reschedule</span>
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md dark:text-gray-100">
                        <DialogHeader>
                            <DialogTitle>Reschedule interview</DialogTitle>
                            <DialogDescription>
                                Choose a new date and time for this interview. The student will be notified once you save the changes.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <label className="block text-xs font-semibold uppercase tracking-wide text-emerald-800">
                                Scheduled for
                            </label>
                            <input
                                type="datetime-local"
                                className="w-full rounded-md border border-emerald-200 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:text-gray-800"
                                value={scheduledFor}
                                onChange={(event) => setScheduledFor(event.target.value)}
                            />
                        </div>
                        <DialogFooter className="sm:justify-between">
                            <Button
                                variant="outline"
                                size="sm"
                                className="cursor-pointer hover:bg-emerald-50 dark:bg-blue-600"
                                onClick={() => setScheduledFor(initialScheduledFor)}
                            >
                                Reset
                            </Button>
                            <Button
                                size="sm"
                                className="cursor-pointer bg-emerald-500 text-white hover:bg-emerald-600 dark:bg-emerald-500 dark:text-white dark:hover:bg-emerald-600"
                                onClick={handleUpdate}
                                disabled={isUpdating}
                            >
                                {isUpdating ? 'Saving...' : 'Save changes'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                {/* === CANCEL WITH YOUR CUSTOM ALERTDIALOG === */}
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button
                            variant="destructive"
                            size="sm"
                            disabled={isCancelling}
                            className="flex items-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" />
                            {isCancelling ? 'Cancelling...' : 'Cancel'}
                        </Button>
                    </AlertDialogTrigger>

                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <div className="flex items-center gap-1">
                                <AlertDialogTitle className="text-md dark:text-gray-100">
                                    Are you sure! want to delete this scheduled interview
                                    ?                                
                                </AlertDialogTitle>
                            </div>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                            <AlertDialogCancel
                                className='bg-blue-500 text-white hover:bg-blue-400 dark:bg-blue-500 dark:hover:bg-blue-700 '>Keep Interview</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleCancel}
                                disabled={isCancelling}
                                className="bg-red-600 hover:bg-red-700 dark:bg-red-500/50 dark:hover:bg-red-700 dark:text-gray-100 border-none"
                            >
                                {isCancelling ? 'Cancelling...' : 'Yes, Cancel Interview'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </div>
    );
};

export default InterviewCardInstructorActions;
